import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { emptyEmployeeBillingAssignment, type U3121EmployeeBillingAssignment, type U3121Project, type U3121Task, type U3121TaskAssignment } from "../../../../U3121Service";
import { NS, U3121ROLES } from "../../../../U3121";
import { useU3121Context } from "../../../../U3121Context";
import type { U3121ProjectFormExtendedHandlers } from "../u3121ProjectContactsTab/U3121ProjectContactHandlers";
import { u3121ProjectEmployeeAssignmentHandler } from "./U3121ProjectEmployeeAssignmentHandlers";
import U3121ProjectEmployeeAssignmentEditDlg from "./U3121ProjectEmployeeAssignmentEditDlg";
import U3121ProjectEmployeeAssignmentDeleteDlg from "./U3121ProjectEmployeeAssignmentDeleteDlg";
import { U2000CurrencyDisplay } from "../../../../../u2000Core/components/tables/display/U2000CurrencyDisplay";

/** Ids des tâches assignées au projet (onglet Assignation des tâches). */
function getAssignedTaskIds(project: U3121Project | null | undefined): Set<string> {
  const raw = project?.taskAssignments;
  if (!raw?.length) return new Set();
  return new Set((raw as (string | U3121TaskAssignment)[]).map((item) => (typeof item === "string" ? item : item.id)));
}

/** Libellé de la tâche selon la langue (Fr/En), avec repli. */
function getTaskLabel(task: U3121Task, language: string): string {
  if (language === "fr" && task.taskDescriptionFr?.trim()) return task.taskDescriptionFr.trim();
  if (language === "en" && task.taskDescriptionEn?.trim()) return task.taskDescriptionEn.trim();
  return task.taskDescription?.trim() || task.taskCode?.trim() || task.id;
}

function getColumnsDef(t: TFunction, language: string): U2000TableColumn[] {
  return [
    {
      ...U2000IdColumnDef,
      display: (column: U2000TableColumn, value: unknown, row: U3121EmployeeBillingAssignment, allowRowSelection: boolean, rowUpdated?: (r: unknown) => void) => {
        const isDisabled = row?.source === "customer";
        const content = (
          <U2000IdCheckedBoxDisplay
            column={column}
            value={value}
            row={(row ?? {}) as unknown as Record<string, unknown>}
            disabled={isDisabled}
            rowUpdated={rowUpdated}
            allowRowSelection={allowRowSelection}
          />
        );
        return isDisabled ? (
          <span title={t("msgEmployeeAssignmentRecordNotModifiable", { ns: NS })} className="inline-flex items-center justify-center w-full">
            {content}
          </span>
        ) : (
          content
        );
      },
    },
    {
      name: "source",
      label: t("lblSource", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, _value: unknown, row: U3121EmployeeBillingAssignment) => {
        if (!row) return "";
        const lang = language;
        if (row.sourceLabelFr != null && row.sourceLabelEn != null) return lang === "en" ? row.sourceLabelEn : row.sourceLabelFr;
        if (row.source === "project") return t("employeeAssignmentSourceProject", { ns: NS });
        if (row.source === "subproject") return t("employeeAssignmentSourceSubproject", { ns: NS });
        if (row.source === "customer") return t("employeeAssignmentSourceCustomer", { ns: NS });
        return t("employeeAssignmentSourceProject", { ns: NS });
      },
    },
    {
      name: "employee",
      label: t("lblEmployee", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[35%]",
      type: "string",
      align: "left",
      display: (_column, _value, row: U3121EmployeeBillingAssignment) => {
        const employee = row.employee;
        if (!employee) return "";
        const name = [employee.firstName, employee.lastName].filter(Boolean).join(" ");
        return employee.codeEmployee ? `${name} (${employee.codeEmployee})` : name;
      },
    },
    {
      name: "task",
      label: t("lblTask", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[35%]",
      type: "string",
      align: "left",
      display: (_column, _value, row: U3121EmployeeBillingAssignment) => {
        const task = row.task;
        if (!task) return "";
        const linguisticLabel = getTaskLabel(task, language);
        const code = task.taskCode?.trim();
        return code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
      },
    },
    {
      name: "hourRate",
      label: t("lblHourlyRate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "number",
      align: "right",
      display: (_column, _value, row: U3121EmployeeBillingAssignment) =>
        row.hourRate === null || row.hourRate === undefined ? (
          <span className="text-gray-500">—</span>
        ) : (
          <U2000CurrencyDisplay value={row.hourRate} align="right" />
        ),
    },
    {
      name: "validFrom",
      label: t("lblValidFrom", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      display: (_column, _value, row: U3121EmployeeBillingAssignment) => row.validFrom ?? "",
    },
    {
      name: "validTo",
      label: t("lblValidTo", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      display: (_column, _value, row: U3121EmployeeBillingAssignment) => row.validTo ?? "",
    },
  ];
}

interface U3121ProjectEmployeeAssignmentBodyProps {
  formStateHandlers: U3121ProjectFormExtendedHandlers;
}

/**
 * Onglet Assignation employés projet : affichage et édition des assignations (employeeBillingAssignments).
 * Même pattern que l'onglet Contact : toolbar Ajouter / Modifier / Supprimer, synchronisation avec le projet parent.
 */
const U3121ProjectEmployeeAssignmentBody: React.FC<U3121ProjectEmployeeAssignmentBodyProps> = ({ formStateHandlers }) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const { initLU } = useU3121Context();
  const project = formStateHandlers.getData?.() ?? null;
  const employeeBillingAssignments = project?.employeeBillingAssignments;
  const allTasks = initLU.tasks ?? [];
  const taskOptions = useMemo(() => {
    const assignedIds = getAssignedTaskIds(project);
    return allTasks.filter((task) => assignedIds.has(task.id));
  }, [allTasks, project]);

  const projectId = project?.id;
  const assignmentsDataKey = React.useMemo(() => {
    const arr = Array.isArray(employeeBillingAssignments) ? employeeBillingAssignments : [];
    return `${projectId ?? ""}_${arr.length}_${arr
      .map((r) => r?.id)
      .filter(Boolean)
      .sort()
      .join(",")}`;
  }, [projectId, employeeBillingAssignments]);

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<U3121EmployeeBillingAssignment>();

  const businessHandlers: U2000FormSBusinessHandlers<U3121EmployeeBillingAssignment> = useMemo(
    () => ({
      newRecord: () => ({ ...emptyEmployeeBillingAssignment }),
      validateForm: (data) => {
        const result: Record<string, string> = {};
        if (data?.validFrom === undefined || data?.validFrom === null || String(data.validFrom).trim() === "") {
          result["validFrom"] = t("errValidFromRequired", { ns: NS });
        }
        // validTo optionnel ; si renseigné, doit être >= validFrom
        const fromStr = data?.validFrom != null ? String(data.validFrom).trim() : "";
        const toStr = data?.validTo != null ? String(data.validTo).trim() : "";
        if (fromStr !== "" && toStr !== "" && toStr < fromStr) {
          result["validTo"] = t("errValidToBeforeValidFrom", { ns: NS });
        }
        const empId = data?.employee?.id?.toString().trim() ?? "";
        const taskId = data?.task?.id?.toString().trim() ?? "";
        const hasEmployeeOrTask = Boolean(empId || taskId);
        if (hasEmployeeOrTask) {
          if (data?.hourRate === undefined || data?.hourRate === null) {
            result["hourRate"] = t("errHourRateRequired", { ns: NS });
          }
        } else if (data?.hourRate !== undefined && data?.hourRate !== null) {
          result["hourRate"] = t("errHourRateWithoutEmployeeOrTask", { ns: NS });
        }
        return result;
      },
    }),
    [t],
  );

  const assignmentFormStateHandlers = useU2000FormStateHandlers<U3121EmployeeBillingAssignment>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const handler = u3121ProjectEmployeeAssignmentHandler({
    gridStateHandlers,
    t,
    formStateHandlers: assignmentFormStateHandlers,
    parentFormHandlers: formStateHandlers,
  });

  useEffect(() => {
    const columns = getColumnsDef(t, i18n.language);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const list = Array.isArray(employeeBillingAssignments) ? employeeBillingAssignments : [];
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu des deps
  }, [assignmentsDataKey, t, i18n.language]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    const proj = formStateHandlers.getData();
    if (!proj) return;
    formStateHandlers.setData({
      ...proj,
      employeeBillingAssignments: gridStateHandlers.getData(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formStateHandlers/gridStateHandlers exclus pour éviter récursion (références instables)
  }, [handler.dataChangedEvent]);

  const isEditMode = formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT;
  const selectedRows = handler.gridStateHandlers.getSelectedRows();
  const hasViewEmployeeRate = hasRole(U3121ROLES.VIEWEMPLOYEERATE);

  const addDisabled = !isEditMode || !hasViewEmployeeRate;
  const modifyDisabled = !isEditMode || selectedRows.length !== 1 || !hasViewEmployeeRate;
  const deleteDisabled = !isEditMode || selectedRows.length === 0 || !hasViewEmployeeRate;

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: addDisabled,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: handler.onModify,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: modifyDisabled,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: deleteDisabled,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, addDisabled, modifyDisabled, deleteDisabled],
  );

  return (
    <>
      <U3121ProjectEmployeeAssignmentEditDlg handler={handler} t={t} NS={NS} taskOptions={taskOptions} />
      <U3121ProjectEmployeeAssignmentDeleteDlg handler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3121EmployeeBillingAssignment)}
        allowRowSelection={isEditMode}
        label={t("lblEmployeeAssignments", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3121ProjectEmployeeAssignmentBody;
